import React, { useEffect, useState } from "react";
import { api, fmtDate, formatErr } from "../../api";
import { SectionTitle, EmptyState, Avatar } from "../../components/Atoms";
import { Megaphone, ChartBar, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const backendBase = process.env.REACT_APP_BACKEND_URL || "";
const imgSrc = (u) => (u ? (u.startsWith("http") ? u : backendBase + u) : "");

function AnnouncementCard({ a }) {
  return (
    <div className="glass p-4" data-testid={`announcement-${a.id}`}>
      <div className="flex items-center gap-3">
        <Avatar src={imgSrc(a.brand_logo)} name={a.brand_name} size={36} />
        <div className="min-w-0 flex-1">
          <div className="text-white font-bold truncate">{a.title}</div>
          <div className="text-[11px] text-muted">{a.brand_name} · {fmtDate(a.created_at)}</div>
        </div>
      </div>
      {a.image_url && (
        <img src={imgSrc(a.image_url)} alt="" className="w-full mt-3 rounded-2xl max-h-72 object-cover" />
      )}
      <div className="text-sm text-white/90 mt-3 whitespace-pre-wrap">{a.message}</div>
    </div>
  );
}

function PollCard({ p, onVote }) {
  const [busy, setBusy] = useState(false);
  const voted = !!p.my_vote;
  const vote = async (optId) => {
    if (voted || busy) return;
    setBusy(true);
    try {
      await api.post(`/polls/${p.id}/vote`, { option_id: optId });
      toast.success("Vote recorded");
      onVote && onVote();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="glass p-4" data-testid={`poll-${p.id}`}>
      <div className="flex items-center gap-3">
        <Avatar src={imgSrc(p.brand_logo)} name={p.brand_name} size={36} />
        <div className="min-w-0 flex-1">
          <div className="text-white font-bold truncate flex items-center gap-1.5">
            <ChartBar size={14} weight="bold" className="text-[#A3E635]" /> {p.question}
          </div>
          <div className="text-[11px] text-muted">{p.brand_name} · {fmtDate(p.created_at)} · {p.total_votes} vote{p.total_votes === 1 ? "" : "s"}</div>
        </div>
      </div>
      {p.image_url && <img src={imgSrc(p.image_url)} alt="" className="w-full mt-3 rounded-2xl max-h-60 object-cover" />}
      <div className="space-y-2 mt-3">
        {p.options.map(o => {
          const pct = p.total_votes ? Math.round((o.votes / p.total_votes) * 100) : 0;
          const mine = p.my_vote === o.id;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => vote(o.id)}
              disabled={voted || busy}
              data-testid={`poll-option-${p.id}-${o.id}`}
              className={`w-full text-left rounded-2xl border overflow-hidden relative ${mine ? "border-[#A3E635]" : "border-white/10"} ${voted ? "cursor-default" : "cursor-pointer hover:border-white/30"} bg-white/5`}
            >
              {voted && <div className="absolute inset-y-0 left-0 bg-[#A3E635]/15" style={{ width: `${pct}%` }} />}
              <div className="relative flex items-center gap-2 px-3 py-2.5">
                {o.image_url && <img src={imgSrc(o.image_url)} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />}
                <span className="text-sm text-white flex items-center gap-2 flex-1 min-w-0 truncate">
                  {mine && <CheckCircle size={14} weight="fill" className="text-[#A3E635] shrink-0" />}
                  {o.label}
                </span>
                {voted && <span className="text-xs text-muted font-bold whitespace-nowrap">{o.votes} · {pct}%</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function AmbassadorAnnouncementsPage() {
  const [tab, setTab] = useState("announcements");
  const [ann, setAnn] = useState([]);
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [a, p] = await Promise.all([api.get("/announcements"), api.get("/polls")]);
      setAnn(a.data || []); setPolls(p.data || []);
    } catch (e) {
      setLoadError(formatErr(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5">
      <SectionTitle title="Brand Updates" subtitle="Announcements & polls from the brands you're linked with" />
      <div className="flex gap-2">
        {[["announcements", `Announcements (${ann.length})`], ["polls", `Polls (${polls.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`updates-tab-${k}`}
            className={`px-4 py-2 rounded-full text-xs font-bold border ${tab === k ? "bg-white/10 border-white/20 text-white" : "bg-white/5 border-white/10 text-muted"}`}>
            {l}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="skeleton h-40" />
      ) : loadError ? (
        <div className="glass p-8 flex flex-col items-center gap-3 text-center">
          <WarningCircle size={32} weight="fill" className="text-[#EC4899]" />
          <div className="text-white font-bold">Couldn't load updates</div>
          <div className="text-muted text-sm">{loadError}</div>
          <button onClick={load} className="btn-primary text-sm px-4 py-2 mt-2">Retry</button>
        </div>
      ) : tab === "announcements" ? (
        ann.length === 0
          ? <EmptyState icon={Megaphone} title="No announcements yet" hint="Once brands post updates for creators linked to their projects, you'll see them here." />
          : <div className="space-y-3">{ann.map(a => <AnnouncementCard key={a.id} a={a} />)}</div>
      ) : (
        polls.length === 0
          ? <EmptyState icon={ChartBar} title="No polls yet" hint="Brand polls addressed to you will appear here." />
          : <div className="space-y-3">{polls.map(p => <PollCard key={p.id} p={p} onVote={load} />)}</div>
      )}
    </div>
  );
}
